import { openDB, type IDBPDatabase } from 'idb';
import type { FullSeafarerData } from '@/lib/types/seafarer';

export type CertStatus =
  | 'valid'
  | 'expiring-soon'
  | 'critical'
  | 'expired'
  /** Permanent record — no renewal required (INDoS, EXN-45 without explicit expiry). */
  | 'no-expiry'
  /** Requirement cannot be determined safely from deterministic Seamigo rules. */
  | 'verify';

export type CertCategory = 'STCW' | 'Medical' | 'Flag State' | 'Endorsement' | 'Proficiency' | 'Other';

export type Certificate = {
  id: string;
  /** Canonical DG/STCW display name after deterministic catalog normalization. */
  name: string;
  /** Exact source text from the DG profile PDF or manual entry before normalization. */
  originalName?: string;
  /** Stable catalog ID used for lifecycle, dedupe, and superseding logic. */
  canonicalId?: string;
  canonicalName?: string;
  shortCode?: string;
  stcwCode?: string;
  certNumber: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
  daysRemaining: number | null;
  status: CertStatus;
  category: CertCategory;
  renewalCourse?: string;
  /** Lifecycle classification — set by certificate-lifecycle-rules classifier. */
  lifecycleType?: string;
  /** Overrides the UI status label when set (e.g. "Refresher due", "No expiry"). */
  actionLabel?: string;
  /** Regulatory note explaining the renewal requirement. */
  requirementNote?: string;
  /** Which lifecycle rule matched this certificate. */
  sourceRuleId?: string;
  /** Provenance: how this certificate was created. Absent for legacy records (treat as dg_import). */
  source?: 'dg_import' | 'manual' | 'user_corrected';
  /** True when the cert is kept for history but another cert owns alerts. */
  alertSuppressed?: boolean;
  /** Name of the certificate that suppresses this cert's expiry alerts. */
  alertSuppressedBy?: string;
  /** Human-readable reason shown in detail views. */
  alertSuppressionReason?: string;
};

const DB_VERSION = 2; // bumped from 1 to add seafarer_profile store

let activeUserId = 'default';
let dbPromise: Promise<IDBPDatabase> | null = null;

// Called by AuthContext whenever the active user changes.
// Resets the DB promise so the next read/write opens the correct per-user database.
export function setActiveUserId(userId: string): void {
  if (userId !== activeUserId) {
    activeUserId = userId;
    dbPromise = null;
  }
}

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    const DB_NAME = `crewnest-${activeUserId}`;
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const certStore = db.createObjectStore('certificates', { keyPath: 'id' });
          certStore.createIndex('status', 'status');
          certStore.createIndex('category', 'category');
          certStore.createIndex('expiryDate', 'expiryDate');
          db.createObjectStore('user_prefs', { keyPath: 'key' });
          db.createObjectStore('parse_history', { keyPath: 'id', autoIncrement: true });
        }
        if (oldVersion < 2) {
          // Single row store — always keyed as 'profile'
          db.createObjectStore('seafarer_profile', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

export const certificatesDb = {
  async getAll(): Promise<Certificate[]> {
    const db = await getDb();
    return db.getAll('certificates');
  },

  async get(id: string): Promise<Certificate | undefined> {
    const db = await getDb();
    return db.get('certificates', id);
  },

  async upsertMany(certs: Certificate[]): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('certificates', 'readwrite');
    await Promise.all([...certs.map((c) => tx.store.put(c)), tx.done]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    return db.delete('certificates', id);
  },

  async replaceAll(certs: Certificate[]): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('certificates', 'readwrite');
    await tx.store.clear();
    await Promise.all([...certs.map((c) => tx.store.put(c)), tx.done]);
  },
};

export const seafarerProfileDb = {
  async get(): Promise<FullSeafarerData | null> {
    const db = await getDb();
    const row = await db.get('seafarer_profile', 'profile');
    return row?.data ?? null;
  },

  async save(data: FullSeafarerData): Promise<void> {
    const db = await getDb();
    await db.put('seafarer_profile', { key: 'profile', data });
  },

  async clear(): Promise<void> {
    const db = await getDb();
    return db.delete('seafarer_profile', 'profile');
  },
};

export const userPrefsDb = {
  async get<T>(key: string): Promise<T | undefined> {
    const db = await getDb();
    const row = await db.get('user_prefs', key);
    return row?.value;
  },

  async set<T>(key: string, value: T): Promise<void> {
    const db = await getDb();
    await db.put('user_prefs', { key, value });
  },
};

export const parseHistoryDb = {
  async add(entry: { fileName: string; parsedAt: string; certCount: number }): Promise<void> {
    const db = await getDb();
    await db.add('parse_history', entry);
  },

  async getAll(): Promise<any[]> {
    const db = await getDb();
    return db.getAll('parse_history');
  },
};
